import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtINR, fmtINRCompact } from "@/lib/format";
import {
    Wallet,
    CreditCard,
    TrendingUp,
    Repeat,
    ShieldCheck,
    BarChart3,
    ArrowRight,
    Receipt,
    ArrowUpFromLine,
    Landmark,
    Target,
    Banknote,
    Zap,
    LineChart,
    FileText,
    Award,
    Wand2,
    Gift,
    RotateCcw,
    KeyRound,
    UserSquare2,
    History,
    Package,
    Activity,
    Smartphone,
    Clock,
    FolderLock,
    ShieldAlert,
    ClipboardCheck,
    RefreshCcw,
    CheckSquare,
    Sparkles,
    Compass,
    Users,
    Car,
    Home as HomeIcon,
    Globe,
    Plane,
    ShieldOff,
    Coins,
} from "lucide-react";

/* Hub configuration — each hub has a summary line + a tile grid.
   Submodules link straight into existing pages so nothing regresses. */
const HUBS = {
    money: {
        title: "Money",
        subtitle: "Everything about the cash that moves in and out.",
        icon: Wallet,
        accent: "emerald",
        summaryFrom: async () => {
            const [ds, nw, bills] = await Promise.all([
                api.get("/dashboard/summary").catch(() => ({ data: null })),
                api.get("/net-worth").catch(() => ({ data: null })),
                api.get("/bills/summary").catch(() => ({ data: null })),
            ]);
            const spend = (ds.data?.business_expenses_month ?? 0) + (ds.data?.personal_expenses_month ?? 0);
            return [
                { label: "Spent this month", value: fmtINR(spend), to: "/expenses" },
                { label: "Card payments made", value: fmtINR(ds.data?.total_paid_this_month), to: "/payments" },
                { label: "Bank balances", value: fmtINR(nw.data?.assets?.bank_balances), to: "/bank-accounts" },
                { label: "Recurring outflow / mo", value: fmtINR(bills.data?.monthly_outflow_estimate), to: "/bills" },
            ];
        },
        tiles: [
            { label: "Expenses", to: "/expenses", icon: Receipt, desc: "Every debit and credit, tagged." },
            { label: "Income", to: "/income", icon: ArrowUpFromLine, desc: "Salary, freelance, receivables." },
            { label: "Bank Accounts", to: "/bank-accounts", icon: Landmark, desc: "Balances, IFSC, UPI." },
            { label: "Budgets", to: "/budgets", icon: Target, desc: "Set caps by card, category or user." },
            { label: "Payments", to: "/payments", icon: Banknote, desc: "Card & bill payments made." },
            { label: "Quick Entry", to: "/quick-entry", icon: Zap, desc: "Log a spend in one line." },
            { label: "Cash Flow Forecast", to: "/cash-flow", icon: LineChart, desc: "Next 30 days at a glance." },
            { label: "Cash Desk", to: "/cash-desk", icon: Coins, desc: "Petty, service & personal cash accounts." },
        ],
    },
    credit: {
        title: "Credit",
        subtitle: "Your credit lines, statements and rewards.",
        icon: CreditCard,
        accent: "blue",
        summaryFrom: async () => {
            const ds = await api.get("/dashboard/summary").catch(() => ({ data: null }));
            const d = ds.data || {};
            return [
                { label: "Active cards", value: (d.active_cards ?? 0).toString(), to: "/cards" },
                { label: "Total outstanding", value: fmtINR(d.total_outstanding), to: "/cards" },
                { label: "Available credit", value: fmtINR(d.available_limit), to: "/cards" },
                { label: "Utilisation", value: `${(d.utilization_pct ?? 0).toFixed(1)}%`, to: "/cards" },
            ];
        },
        tiles: [
            { label: "Credit Cards", to: "/cards", icon: CreditCard, desc: "The card master — 40+ cards." },
            { label: "Statements", to: "/statements", icon: FileText, desc: "Upload PDFs, OCR extracts." },
            { label: "Payments", to: "/payments", icon: Banknote, desc: "Post & reconcile payments." },
            { label: "Offers & Rewards", to: "/offers", icon: Gift, desc: "Card offers, expiry tracking." },
            { label: "Fee Waiver", to: "/annual-fee-waiver", icon: Award, desc: "Cycle-by-cycle waiver tracker." },
            { label: "Card Advisor", to: "/card-advisor", icon: Wand2, desc: "Best-card recommender." },
            { label: "Refunds & Reversals", to: "/refunds", icon: RotateCcw, desc: "Chargebacks, reversals." },
            { label: "Password Vault", to: "/statement-passwords", icon: KeyRound, desc: "Encrypted, second-lock." },
            { label: "Card Holders", to: "/card-holders", icon: UserSquare2, desc: "Family & employee master." },
            { label: "Card History", to: "/card-history", icon: History, desc: "Closures, replacements." },
            { label: "Credit Score", to: "/credit-score", icon: TrendingUp, desc: "Bureau history + trend." },
        ],
    },
    wealth: {
        title: "Wealth",
        subtitle: "What you own — investments, property, jewellery.",
        icon: TrendingUp,
        accent: "violet",
        summaryFrom: async () => {
            const [nw, inv] = await Promise.all([
                api.get("/net-worth").catch(() => ({ data: null })),
                api.get("/investments/summary").catch(() => ({ data: null })),
            ]);
            return [
                { label: "Net worth", value: fmtINR(nw.data?.net_worth), to: "/hub/wealth" },
                { label: "Investments", value: fmtINR(inv.data?.total_current), to: "/investments" },
                { label: "Asset registry", value: fmtINR(nw.data?.assets?.assets_registry), to: "/assets" },
                { label: "Bank balances", value: fmtINR(nw.data?.assets?.bank_balances), to: "/bank-accounts" },
            ];
        },
        tiles: [
            { label: "Investments", to: "/investments", icon: LineChart, desc: "Stocks, MFs, SIPs, gold, EPF." },
            { label: "Assets", to: "/assets", icon: Package, desc: "Electronics, jewellery, art." },
            { label: "Net Worth (weekly)", to: "/investments", icon: TrendingUp, desc: "Weekly snapshot series." },
            { label: "Health Score", to: "/health-score", icon: Activity, desc: "7-factor financial health." },
            { label: "Milestones", to: "/milestones", icon: Award, desc: "Reward-earning milestones." },
        ],
    },
    commitments: {
        title: "Commitments",
        subtitle: "Recurring outflows and long-term obligations.",
        icon: Repeat,
        accent: "amber",
        summaryFrom: async () => {
            const bills = await api.get("/bills/summary").catch(() => ({ data: null }));
            const subs = await api.get("/subscriptions").catch(() => ({ data: [] }));
            const activeSubs = (Array.isArray(subs.data) ? subs.data : []).filter((s) => !s.archived).length;
            return [
                { label: "Active bills", value: (bills.data?.active_bills ?? 0).toString(), to: "/bills" },
                { label: "Est. outflow / mo", value: fmtINR(bills.data?.monthly_outflow_estimate), to: "/bills" },
                { label: "Active subscriptions", value: activeSubs.toString(), to: "/subscriptions" },
                { label: "Missing invoices", value: (bills.data?.missing_invoices_prev_month ?? 0).toString(), to: "/bills" },
            ];
        },
        tiles: [
            { label: "Recurring Bills", to: "/bills", icon: Smartphone, desc: "Mobile, DTH, OTT, utilities." },
            { label: "Subscriptions", to: "/subscriptions", icon: Repeat, desc: "SaaS & OTT tracker." },
        ],
    },
    protection: {
        title: "Protection",
        subtitle: "Coverage, documents and expiry tracking.",
        icon: ShieldCheck,
        accent: "rose",
        summaryFrom: async () => {
            const [renewals, docs] = await Promise.all([
                api.get("/renewals").catch(() => ({ data: [] })),
                api.get("/documents").catch(() => ({ data: [] })),
            ]);
            const rlist = Array.isArray(renewals.data) ? renewals.data : [];
            const dlist = Array.isArray(docs.data) ? docs.data : [];
            const soon = rlist.filter((r) => {
                if (!r.expiry_date) return false;
                const days = (new Date(r.expiry_date).getTime() - Date.now()) / 86400000;
                return days >= 0 && days <= 30;
            }).length;
            return [
                { label: "Tracked renewals", value: rlist.length.toString(), to: "/renewals" },
                { label: "Expiring in 30 days", value: soon.toString(), to: "/renewals" },
                { label: "Documents on file", value: dlist.length.toString(), to: "/documents" },
            ];
        },
        tiles: [
            { label: "Renewals", to: "/renewals", icon: Clock, desc: "Insurance, warranties, AMCs." },
            { label: "Documents", to: "/documents", icon: FolderLock, desc: "Encrypted document store." },
            { label: "Disputes", to: "/disputes", icon: ShieldAlert, desc: "Chargebacks & follow-ups." },
            { label: "Security Center", to: "/security", icon: ShieldCheck, desc: "TOTP, sessions." },
        ],
    },
    insights: {
        title: "Insights",
        subtitle: "Reports, reconciliation, audit and analytics.",
        icon: BarChart3,
        accent: "neutral",
        summaryFrom: async () => {
            const [fr, dh] = await Promise.all([
                api.get("/finance-review").catch(() => ({ data: null })),
                api.get("/data-health/score").catch(() => ({ data: null })),
            ]);
            return [
                { label: "Data health", value: `${dh.data?.score ?? "—"}/100`, to: "/finance-review" },
                { label: "Unreconciled statements", value: (fr.data?.counts?.unreconciled_statements ?? fr.data?.unreconciled_statements?.length ?? 0).toString(), to: "/reconciliation" },
                { label: "Payments to reconcile", value: (fr.data?.counts?.payments_not_reflected ?? 0).toString(), to: "/reconciliation" },
                { label: "Unapproved expenses", value: (fr.data?.counts?.unapproved_expenses ?? 0).toString(), to: "/expenses" },
            ];
        },
        tiles: [
            { label: "Action Center", to: "/finance-review", icon: ClipboardCheck, desc: "Everything needing action." },
            { label: "Reports", to: "/reports", icon: BarChart3, desc: "Monthly, card, spend, YTD." },
            { label: "Reconciliation", to: "/reconciliation", icon: RefreshCcw, desc: "Match txns ↔ payments." },
            { label: "Monthly Closing", to: "/monthly-closing", icon: CheckSquare, desc: "Month-end sign-off." },
            { label: "Tax Centre", to: "/tax-centre", icon: FileText, desc: "80C/80D/HRA + AI optimiser." },
            { label: "Exception Centre", to: "/exceptions", icon: ShieldOff, desc: "Anomaly aggregation." },
            { label: "Copilot", to: "/copilot", icon: Sparkles, desc: "Ask about your finances." },
            { label: "Life Timeline", to: "/timeline", icon: Sparkles, desc: "Journey of your money." },
            { label: "Journeys", to: "/journeys", icon: Compass, desc: "Life-moment templates." },
            { label: "Family View", to: "/family", icon: Users, desc: "Per-owner rollup." },
            { label: "Vehicles", to: "/vehicles", icon: Car, desc: "Fuel, service, EMI logs." },
            { label: "Home Hub", to: "/home-hub", icon: HomeIcon, desc: "Home construction & interiors." },
            { label: "Forex Cards", to: "/forex-cards", icon: Globe, desc: "International money features." },
            { label: "Trips", to: "/trips", icon: Plane, desc: "Trip wrapper for forex expenses." },
        ],
    },
};

const ACCENT_CLS = {
    emerald: "bg-emerald-50 text-emerald-600",
    blue: "bg-blue-50 text-blue-600",
    violet: "bg-violet-50 text-violet-600",
    amber: "bg-amber-50 text-amber-600",
    rose: "bg-rose-50 text-rose-600",
    neutral: "bg-stone-100 text-stone-600",
};

export default function ModuleHub() {
    const { slug } = useParams();
    const cfg = HUBS[slug];
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!cfg?.summaryFrom) return;
        let cancelled = false;
        setLoading(true);
        cfg.summaryFrom()
            .then((s) => { if (!cancelled) setSummary(s); })
            .catch(() => { if (!cancelled) setSummary([]); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [slug]);

    if (!cfg) return <Navigate to="/dashboard" replace />;

    const Icon = cfg.icon;
    return (
        <div className="max-w-[1400px] mx-auto" data-testid={`hub-${slug}`}>
            {/* Header */}
            <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
                <div className="flex items-start gap-4">
                    <span className={`w-14 h-14 rounded-2xl flex items-center justify-center ${ACCENT_CLS[cfg.accent]}`}>
                        <Icon size={26} strokeWidth={1.6} />
                    </span>
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.2em] text-stone-500 font-semibold mb-1">
                            Module hub
                        </div>
                        <h1 className="text-2xl md:text-3xl font-bold text-stone-900 tracking-tight">{cfg.title}</h1>
                        <p className="text-sm text-stone-500 mt-1">{cfg.subtitle}</p>
                    </div>
                </div>
                <Link
                    to="/dashboard"
                    className="text-[13px] font-medium text-stone-600 hover:text-stone-900 inline-flex items-center gap-1"
                    data-testid="hub-back-home"
                >
                    ← Back to Home
                </Link>
            </div>

            {/* Summary row */}
            {summary && summary.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="hub-summary">
                    {summary.map((s) => (
                        <Link
                            key={s.label}
                            to={s.to}
                            className="bg-white border border-stone-200 rounded-2xl p-4 hover:border-stone-300 transition-colors"
                            data-testid={`hub-metric-${s.label.replace(/\s+/g, "-").toLowerCase()}`}
                        >
                            <div className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold">{s.label}</div>
                            <div className="text-lg font-bold text-stone-900 tabular-nums mt-1">{loading ? "…" : s.value}</div>
                        </Link>
                    ))}
                </div>
            )}

            {/* Tile grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {cfg.tiles.map((t) => (
                    <Link
                        key={t.to + t.label}
                        to={t.to}
                        className="bg-white border border-stone-200 rounded-2xl p-4 hover:border-stone-300 hover:shadow-sm transition-all group"
                        data-testid={`hub-tile-${t.label.replace(/\s+/g, "-").toLowerCase()}`}
                    >
                        <div className="flex items-start justify-between mb-2">
                            <span className={`w-9 h-9 rounded-lg flex items-center justify-center ${ACCENT_CLS[cfg.accent]}`}>
                                <t.icon size={15} strokeWidth={1.8} />
                            </span>
                            <ArrowRight size={13} className="text-stone-300 group-hover:text-stone-700 group-hover:translate-x-0.5 transition-all" />
                        </div>
                        <div className="text-[15px] font-semibold text-stone-900">{t.label}</div>
                        <div className="text-[12px] text-stone-500 mt-0.5 leading-snug">{t.desc}</div>
                    </Link>
                ))}
            </div>
        </div>
    );
}
